import round from 'lodash/round';

import { Node, NodeStorageMetrics } from '@cast/types';
import { convertUnit, toNumOrZero } from '@cast/utils';

import { getResourceUsagePercentage } from 'utils/node';

import { EphemeralStorageMetrics, ResourceUsageMetrics } from '../types';

export type NodeMetrics = {
  cpuMetrics: ResourceUsageMetrics;
  memoryMetrics: ResourceUsageMetrics;
  ephemeralStorageMetrics?: EphemeralStorageMetrics;
};

export const getNodeMetrics = (
  node?: Node,
  storageMetrics?: NodeStorageMetrics
): NodeMetrics => {
  const resources = node?.resources;
  const cpuMetrics: ResourceUsageMetrics = {
    usage:
      getResourceUsagePercentage(
        resources?.cpuRequestsMilli,
        resources?.cpuAllocatableMilli
      ) ?? 0,
    requested: resources?.cpuRequestsMilli
      ? round(resources.cpuRequestsMilli / 1000, 3)
      : 0,
    allocatable: resources?.cpuAllocatableMilli
      ? round(resources.cpuAllocatableMilli / 1000, 3)
      : 0,
    capacity: resources?.cpuCapacityMilli
      ? round(resources.cpuCapacityMilli / 1000, 3)
      : 0,
  };

  const memoryMetrics: ResourceUsageMetrics = {
    usage:
      getResourceUsagePercentage(
        resources?.memRequestsMib,
        resources?.memAllocatableMib
      ) ?? 0,
    requested: resources?.memRequestsMib
      ? round(convertUnit(resources.memRequestsMib, 'MiB', 'GiB'), 3)
      : 0,
    allocatable: resources?.memAllocatableMib
      ? round(convertUnit(resources.memAllocatableMib, 'MiB', 'GiB'), 3)
      : 0,
    capacity: resources?.memCapacityMib
      ? round(convertUnit(resources.memCapacityMib, 'MiB', 'GiB'), 3)
      : 0,
  };

  const ephemeralStorageMetrics = storageMetrics
    ? (() => {
        const allocatableBytes = toNumOrZero(storageMetrics.allocatableBytes);
        const requestedBytes = toNumOrZero(storageMetrics.requestedBytes);
        const usedBytes = toNumOrZero(storageMetrics.usedBytes);

        return {
          usage:
            getResourceUsagePercentage(requestedBytes, allocatableBytes) ?? 0,
          requested: round(convertUnit(requestedBytes, 'Bytes', 'GiB'), 2),
          used: round(convertUnit(usedBytes, 'Bytes', 'GiB'), 2),
          allocatable: round(convertUnit(allocatableBytes, 'Bytes', 'GiB'), 2),
          capacity: round(convertUnit(allocatableBytes, 'Bytes', 'GiB'), 2),
          hasRequests: requestedBytes > 0,
        };
      })()
    : undefined;

  return { cpuMetrics, memoryMetrics, ephemeralStorageMetrics };
};
